import APIService from "./APIService";

/**
 * UserGroupAPI - Service for managing user groups
 * Handles operations such as creating groups, managing memberships,
 * and retrieving group information.
 *
 * SECURITY: All methods use groupName instead of groupId to prevent IDOR attacks.
 * GroupId is never exposed to the frontend.
 */
class UserGroupAPI {
  /**
   * Get the authenticated user's group
   * SECURITY: Uses context-based endpoint - no groupId required
   *
   * @returns {Promise} - Promise resolving to the user's group details (without groupId)
   */
  static async getMyGroup() {
    try {
      return await APIService.get(`/api/v1/base/groups/my-group`);
    } catch (error) {
      console.error("Error fetching my group:", error);
      throw error;
    }
  }

  /**
   * Get all groups for the authenticated user's account
   * SECURITY: Uses context-based endpoint - accountId derived from authenticated user
   *
   * @returns {Promise} - Promise resolving to an array of groups (without groupId)
   */
  static async getGroupsForMyAccount() {
    try {
      return await APIService.get(`/api/v1/base/groups/my-account`);
    } catch (error) {
      console.error("Error fetching groups for my account:", error);
      throw error;
    }
  }

  /**
   * Get details for a specific group by name
   * SECURITY: Uses groupName instead of groupId
   *
   * @param {string} groupName - The name of the group
   * @returns {Promise} - Promise resolving to the group details (without groupId)
   */
  static async getGroupByName(groupName) {
    try {
      return await APIService.get(
        `/api/v1/base/groups/by-name/${encodeURIComponent(groupName)}`,
      );
    } catch (error) {
      console.error("Error fetching group by name:", error);
      throw error;
    }
  }

  /**
   * Create a new user group
   * SECURITY: AccountId is derived from authenticated user on backend
   *
   * @param {Object} groupData - The group data including groupName and description
   * @returns {Promise} - Promise resolving to the created group (without groupId)
   */
  static async createGroup(groupData) {
    try {
      return await APIService.post("/api/v1/base/groups/create", groupData);
    } catch (error) {
      console.error("Error creating group:", error);
      throw error;
    }
  }

  /**
   * Update an existing group by name
   * SECURITY: Uses groupName instead of groupId
   *
   * @param {string} groupName - The name of the group to update
   * @param {Object} groupData - The updated group data (newGroupName, description)
   * @returns {Promise} - Promise resolving to the updated group (without groupId)
   */
  static async updateGroupByName(groupName, groupData) {
    try {
      return await APIService.put(
        `/api/v1/base/groups/by-name/${encodeURIComponent(groupName)}`,
        groupData,
      );
    } catch (error) {
      console.error("Error updating group:", error);
      throw error;
    }
  }

  /**
   * Delete a group by name
   * SECURITY: Uses groupName instead of groupId
   *
   * @param {string} groupName - The name of the group to delete
   * @returns {Promise} - Promise resolving to deletion confirmation
   */
  static async deleteGroupByName(groupName) {
    try {
      return await APIService.delete(
        `/api/v1/base/groups/by-name/${encodeURIComponent(groupName)}`,
      );
    } catch (error) {
      console.error("Error deleting group:", error);
      throw error;
    }
  }

  /**
   * Search groups by name
   *
   * @param {string} searchQuery - The search query
   * @returns {Promise} - Promise resolving to an array of matching groups (without groupId)
   */
  static async searchGroups(searchQuery) {
    try {
      return await APIService.get(
        `/api/v1/base/groups/search?name=${encodeURIComponent(searchQuery)}`,
      );
    } catch (error) {
      console.error("Error searching groups:", error);
      throw error;
    }
  }

  // ==================== DEPRECATED METHODS ====================
  // These methods expose groupId and should be migrated to secure alternatives

  /**
   * @deprecated Use getGroupByName() instead. This method exposes groupId.
   */
  static async getGroupDetails(groupId) {
    console.warn(
      "DEPRECATED: getGroupDetails exposes groupId - use getGroupByName() instead",
    );
    try {
      return await APIService.get(`/api/v1/base/groups/${groupId}`);
    } catch (error) {
      console.error("Error fetching group details:", error);
      throw error;
    }
  }

  /**
   * @deprecated Use updateGroupByName() instead. This method exposes groupId.
   */
  static async updateGroup(groupId, groupData) {
    console.warn(
      "DEPRECATED: updateGroup exposes groupId - use updateGroupByName() instead",
    );
    try {
      return await APIService.put(`/api/v1/base/groups/${groupId}`, groupData);
    } catch (error) {
      console.error("Error updating group:", error);
      throw error;
    }
  }

  /**
   * @deprecated This method exposes userId. Consider using context-based endpoints.
   */
  static async getUserGroups(userId) {
    console.warn("DEPRECATED: getUserGroups exposes userId");
    try {
      return await APIService.get(`/api/v1/base/users/${userId}/groups`);
    } catch (error) {
      console.error("Error fetching user groups:", error);
      throw error;
    }
  }

  /**
   * @deprecated Use getGroupsForMyAccount() instead. This method exposes accountId.
   */
  static async getUserGroupsByAccountId(accountId) {
    console.warn(
      "DEPRECATED: getUserGroupsByAccountId exposes accountId - use getGroupsForMyAccount() instead",
    );
    try {
      // Using the correct endpoint from UserGroupController: /account/{accountId}
      return await APIService.get(`/api/v1/base/groups/account/${accountId}`);
    } catch (error) {
      console.error("Error fetching groups for account:", error);
      throw error;
    }
  }
}

export default UserGroupAPI;
